import { createMongoAbility, subject, type RawRuleOf } from '@casl/ability'
import { unpackRules } from '@casl/ability/extra'
import {
  AccessSubjectType,
  Permission,
  PermissionLevel,
  type AccessAbility,
  type AccessMe,
  type AccessScope,
} from '~/types/v2/Access'

/** Where each permission applies. Mirrors studio-api's `PERMISSION_LEVELS`. */
export const PERMISSION_LEVELS: Readonly<Record<Permission, PermissionLevel>> =
  {
    [Permission.OrganizationView]: PermissionLevel.Organization,
    [Permission.OrganizationSettingsManage]: PermissionLevel.Organization,
    [Permission.OrganizationMembersManage]: PermissionLevel.Organization,
    [Permission.OrganizationOwnersManage]: PermissionLevel.Organization,
    [Permission.OrganizationWorkspacesManage]: PermissionLevel.Organization,
    [Permission.OrganizationBillingManage]: PermissionLevel.Organization,

    [Permission.WorkspaceView]: PermissionLevel.Workspace,
    [Permission.ContentView]: PermissionLevel.Workspace,
    [Permission.SubmissionsView]: PermissionLevel.Workspace,
    [Permission.GradingRun]: PermissionLevel.Workspace,
    [Permission.ContentManage]: PermissionLevel.Workspace,
    [Permission.SubmissionsExport]: PermissionLevel.Workspace,
    [Permission.WorkspaceSettingsManage]: PermissionLevel.Workspace,

    [Permission.PlatformOrganizationsManage]: PermissionLevel.Platform,
    [Permission.PlatformUsersManage]: PermissionLevel.Platform,
    [Permission.PlatformSettingsManage]: PermissionLevel.Platform,
    [Permission.PlatformBillingManage]: PermissionLevel.Platform,
  }

/** Reverses the API's `packRules`. */
export function unpackAccessRules(
  packedRules: AccessMe['rules'],
): RawRuleOf<AccessAbility>[] {
  return unpackRules(packedRules) as RawRuleOf<AccessAbility>[]
}

/** An ability holding the given packed rules, or none. */
export function createAccessAbility(
  packedRules: AccessMe['rules'] = [],
): AccessAbility {
  return createMongoAbility<AccessAbility>(unpackAccessRules(packedRules))
}

/**
 * The subject a permission is checked against, built from the scope. Null when
 * the scope lacks an id the permission's level needs.
 *
 * Always a subject with its ids, never a bare type name: CASL answers
 * `can(permission, 'Workspace')` yes for anyone who has the permission in some
 * workspace or other. A workspace subject carries its organisation because an
 * Owner's and an Org admin's workspace rules match on `organizationId`.
 */
export function accessSubject(permission: Permission, scope: AccessScope) {
  switch (PERMISSION_LEVELS[permission]) {
    case PermissionLevel.Platform:
      return subject(AccessSubjectType.Platform, {})
    case PermissionLevel.Organization:
      if (!scope.organizationId) return null
      return subject(AccessSubjectType.Organization, {
        id: scope.organizationId,
      })
    case PermissionLevel.Workspace:
      if (!scope.workspaceId || !scope.organizationId) return null
      return subject(AccessSubjectType.Workspace, {
        id: scope.workspaceId,
        organizationId: scope.organizationId,
      })
    default:
      return null
  }
}

/**
 * Whether the ability allows the permission in the scope. A scope missing an
 * id the permission needs answers no, so a check made before the organisation
 * has resolved hides the control rather than showing it.
 */
export function canAccess(
  ability: AccessAbility,
  permission: Permission,
  scope: AccessScope = {},
): boolean {
  const target = accessSubject(permission, scope)
  return target ? ability.can(permission, target) : false
}
